/**
 * @file messageApi.js
 * @description 消息域 API（入参/出参与存量保持一致）
 * @module features/message/api/messageApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'

/**
 * @param {string} token
 * @returns {Record<string, string>}
 */
function buildAuthHeaders(token) {
  return {
    'COMIC-WEB-TOKEN': token || '',
  }
}

/**
 * 消息分页（留言/评论）
 * @param {{ type: string, pageNum: number, pageSize: number, messageId?: string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchMessagePageApi(payload, token = '') {
  return axios.post(API.MESSAGE.PAGE, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 新增消息（留言/评论）
 * @param {{ content: string, type: string, belongId?: string, parentId?: string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function addMessageApi(payload, token = '') {
  return axios.post(API.MESSAGE.ADD, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 回复分页
 * @param {{ messageId: string, type: string, pageNum: number, pageSize: number }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchMessageReplyPageApi(payload, token = '') {
  return axios.post(API.MESSAGE.REPLY_PAGE, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 回复消息
 * @param {{ content: string, parentId: string, type: string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function replyMessageApi(payload, token = '') {
  return axios.post(API.MESSAGE.REPLY, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 删除消息
 * @param {string} messageId
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function deleteMessageApi(messageId, token = '') {
  return axios.post(
    API.MESSAGE.DELETE,
    { messageId: String(messageId) },
    {
      headers: buildAuthHeaders(token),
    }
  )
}

/**
 * 点赞
 * @param {{ type: string, targetId: string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function insertMessageLikeApi(payload, token = '') {
  return axios.post(API.MESSAGE.LIKE_INSERT, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 取消点赞
 * @param {{ type: string, targetId: string }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function cancelMessageLikeApi(payload, token = '') {
  return axios.post(API.MESSAGE.LIKE_CANCEL, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 互动消息 SSE 未读流 URL（供 startInteractionUnreadStream 使用）
 * @returns {string}
 */
export function getInteractionUnreadStreamUrl() {
  return API.MESSAGE.INTERACTION_UNREAD_STREAM
}

/**
 * 互动消息未读数
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchInteractionUnreadCountApi(token = '') {
  return axios.post(API.MESSAGE.INTERACTION_UNREAD_COUNT, {}, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 互动消息分页
 * @param {{ pageNum: number, pageSize: number }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchInteractionPageApi(payload, token = '') {
  return axios.post(API.MESSAGE.INTERACTION_PAGE, payload, {
    headers: buildAuthHeaders(token),
  })
}

/**
 * 标记互动消息已读（单条或全部）
 * @param {string} interactionMessageId 空字符串表示全部已读
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function markInteractionReadApi(interactionMessageId, token = '') {
  return axios.post(
    API.MESSAGE.INTERACTION_READ,
    { interactionMessageId: interactionMessageId ?? '' },
    {
      headers: buildAuthHeaders(token),
    }
  )
}
